import { useEffect, useRef, useState } from "react";

const emptyPasswords = () => ({ currentPassword: "", newPassword: "", confirmPassword: "" });
export const PASSWORD_POLICY = "Use at least 8 characters and no more than 72 bytes (some characters use more than one byte).";
const serviceMessage = "We couldn't confirm the password update. Try again later. If it continues, contact support or use Forgot Password.";

export function passwordChangeBase() {
  const configured = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND || process.env.REACT_APP_API_URL;
  return (configured || "https://cphere-backend.onrender.com").trim().replace(/\/+$/, "").replace(/\/api$/i, "");
}

export function passwordChangeError(status, code) {
  if (status === 401) return "Your session has expired. Sign in again to change your password.";
  if (status === 429) return "Too many attempts. Wait 15 minutes before trying again.";
  if (status === 403 && code === "reauthentication_failed") return "Your current password could not be verified. Try again or use Forgot Password.";
  if (status === 403) return "Password changes are unavailable for this session. Sign in again or contact support.";
  if (status === 400) {
    if (code === "password_policy") return PASSWORD_POLICY;
    if (code === "password_unchanged") return "Choose a password different from your current password.";
    if (code === "current_password_required") return "Enter your current password to continue.";
    return "Check the password fields and try again.";
  }
  return serviceMessage;
}

export function isPasswordChangeSessionCurrent(getToken, changedToken) {
  if (typeof getToken !== "function") return false;
  try { return getToken() === changedToken; } catch (_) { return false; }
}

export default function usePasswordChange(getToken, onConfirmed = null, onSessionExpired = null) {
  const [showPopup, setShowPopup] = useState(false);
  const [passwords, setPasswords] = useState(emptyPasswords);
  const [passwordError, setPasswordError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expired, setExpired] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const handleOpenPopup = () => {
    setPasswords(emptyPasswords()); setPasswordError(""); setSaved(false); setExpired(false); setShowPopup(true);
  };
  const handleClosePopup = () => {
    if (inFlight.current) return;
    setPasswords(emptyPasswords()); setPasswordError(""); setShowPopup(false);
  };
  const handlePasswordChange = ({ target: { name, value } }) => {
    if (inFlight.current) return;
    setPasswords((previous) => ({ ...previous, [name]: value }));
    setPasswordError("");
  };
  const handleSavePassword = async (event) => {
    event?.preventDefault();
    if (inFlight.current || saved || expired) return;
    setPasswordError("");
    const { currentPassword, newPassword, confirmPassword } = passwords;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Enter your current password and both new password fields."); return;
    }
    if (newPassword !== confirmPassword) { setPasswordError("Passwords do not match."); return; }
    if (newPassword.length < 8 || new Blob([newPassword]).size > 72) {
      setPasswordError(PASSWORD_POLICY); return;
    }
    let token = null;
    try { token = getToken() || null; } catch (_) {}
    if (!token) {
      try { onSessionExpired?.(token); } catch (_) {}
      if (!mounted.current) return;
      setPasswords(emptyPasswords()); setExpired(true); setPasswordError(passwordChangeError(401)); return;
    }
    inFlight.current = true;
    setSaving(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${passwordChangeBase()}/api/auth/update-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, password: newPassword }),
        signal: controller.signal,
        cache: "no-store",
      });
      const data = await response.json().catch(() => null);
      const sessionIsCurrent = isPasswordChangeSessionCurrent(getToken, token);
      if (!sessionIsCurrent) {
        if (mounted.current) {
          setPasswords(emptyPasswords()); setSaved(false); setExpired(false);
          setPasswordError(""); setShowPopup(false);
        }
        return;
      }
      if (response.ok && data?.ok === true && data?.reauthenticate === true) {
        // The backend has revoked this session. Clear the portal's local auth
        // even if navigation unmounted this form while the request was pending.
        try { onConfirmed?.(token); } catch (_) {}
        if (!mounted.current) return;
        setPasswords(emptyPasswords()); setSaved(true);
      } else {
        if (response.status === 401) {
          // Clear cached portal auth immediately, including when navigation
          // unmounted this form while its request was in flight. The caller
          // verifies that this is still the token which made the request.
          try { onSessionExpired?.(token); } catch (_) {}
          if (!mounted.current) return;
          setPasswords(emptyPasswords());
        }
        if (!mounted.current) return;
        setExpired(response.status === 401);
        setPasswordError(passwordChangeError(response.status, data?.code));
      }
    } catch (_) {
      if (!mounted.current) return;
      if (!isPasswordChangeSessionCurrent(getToken, token)) {
        setPasswords(emptyPasswords()); setSaved(false); setExpired(false);
        setPasswordError(""); setShowPopup(false);
      } else {
        setPasswordError(serviceMessage);
      }
    } finally {
      clearTimeout(timeout);
      inFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  return { showPopup, passwords, passwordError, saving, saved, expired, handleOpenPopup, handleClosePopup, handlePasswordChange, handleSavePassword };
}
